import { useState, useEffect, useRef } from "react";
import {
  FaUserClock,
  FaClock,
  FaCalendarDay,
  FaLocationDot,
  FaTag,
  FaUserCheck,
  FaXmark,
  FaRightToBracket,
  FaRightFromBracket,
  FaMagnifyingGlass,
  FaRotate,
  FaCheckDouble,
  FaTriangleExclamation,
  FaChevronDown,
  FaLink,
} from "react-icons/fa6";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";

/* ============================================
   HELPER FUNCTIONS
   ============================================ */
const formatTimeWIB = (date) => {
  return new Date(date).toLocaleTimeString("id-ID", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const getNowTimeInput = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

const getNowDateInput = () => {
  const now = new Date();
  return now.toISOString().split("T")[0];
};

/* ============================================
   KOMPONEN: Toast
   ============================================ */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor =
    type === "success"
      ? "border-emerald-500/30 bg-emerald-900/90"
      : "border-red-500/30 bg-red-900/90";
  const icon =
    type === "success" ? (
      <FaCheckDouble className="text-emerald-400" />
    ) : (
      <FaTriangleExclamation className="text-red-400" />
    );

  return (
    <div
      className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-3.5 rounded-xl border backdrop-blur-md text-white shadow-2xl ${bgColor}`}
      style={{ animation: "slideIn 0.3s ease-out" }}
    >
      {icon}
      <span className="text-sm font-medium">{message}</span>
    </div>
  );
};

/* ============================================
   KOMPONEN: Employee Search Dropdown
   ============================================ */
const EmployeeSearch = ({
  employees,
  selected,
  onSelect,
  loading,
  error,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const selectedEmployee = employees.find((e) => e.id === selected);

  // Filter berdasarkan nama atau NIK
  const filtered = query.trim()
    ? employees.filter(
      (e) =>
        e.name?.toLowerCase().includes(query.toLowerCase()) ||
        e.nik?.toLowerCase().includes(query.toLowerCase()) ||
        e.employee_id?.toLowerCase().includes(query.toLowerCase())
    )
    : employees;

  // Close dropdown saat klik di luar
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlight saat query berubah
  useEffect(() => {
    setHighlightIndex(-1);
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightIndex((prev) =>
          prev > 0 ? prev - 1 : filtered.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightIndex >= 0 && filtered[highlightIndex]) {
          handleSelect(filtered[highlightIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (emp) => {
    onSelect(emp.id);
    setQuery("");
    setIsOpen(false);
  };

  const handleClear = () => {
    onSelect(null);
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        <span className="inline-flex items-center gap-1.5">
          <FaUserCheck className="text-cyan-500/70" />
          Pilih Karyawan
        </span>
      </label>

      {/* Input Area */}
      <div
        className={`flex items-center gap-2 px-3.5 py-3 rounded-xl transition-all duration-200 ${error
            ? "border border-red-500/40 bg-red-500/5"
            : isOpen
              ? "border border-cyan-500/40 bg-slate-800/60"
              : "border border-slate-600/30 bg-slate-800/40 hover:border-slate-500/40"
          }`}
        style={{ backdropFilter: "blur(8px)" }}
      >
        <FaMagnifyingGlass className="text-slate-500 text-sm flex-shrink-0" />

        {selectedEmployee ? (
          <div className="flex-1 flex items-center justify-between min-w-0">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-100 truncate">
                {selectedEmployee.name}
              </p>
              {selectedEmployee.nik && (
                <p className="text-[11px] text-slate-400 font-mono">
                  {selectedEmployee.nik}
                </p>
              )}
              {selectedEmployee.department && (
                <p className="text-[11px] text-slate-500">
                  {selectedEmployee.department}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="ml-2 p-1 rounded-lg hover:bg-slate-700/60 text-slate-400 hover:text-red-400 transition-colors flex-shrink-0"
            >
              <FaXmark className="text-sm" />
            </button>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Cari nama atau NIK karyawan..."
            disabled={loading}
            className="flex-1 bg-transparent text-sm text-gray-100 placeholder-slate-500 outline-none disabled:opacity-50"
          />
        )}

        <FaChevronDown
          className={`text-slate-500 text-xs transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""
            }`}
        />
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1.5 text-xs text-red-400">{error}</p>
      )}

      {/* Dropdown List */}
      {isOpen && !selectedEmployee && (
        <div
          className="absolute z-50 w-full mt-1.5 rounded-xl overflow-hidden shadow-2xl"
          style={{
            background: "rgba(15, 23, 42, 0.95)",
            border: "1px solid rgba(51, 65, 85, 0.4)",
            backdropFilter: "blur(16px)",
            maxHeight: "280px",
          }}
        >
          {loading ? (
            <div className="px-4 py-6 text-center">
              <i className="fa-solid fa-spinner fa-spin text-cyan-400" />
              <p className="text-xs text-slate-400 mt-2">Memuat data...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <FaUserClock className="text-slate-600 text-xl mx-auto mb-2" />
              <p className="text-sm text-slate-400">
                {query ? 'Tidak ditemukan "' + query + '"' : "Tidak ada data karyawan"}
              </p>
            </div>
          ) : (
            <ul className="overflow-y-auto" style={{ maxHeight: "280px" }}>
              {filtered.map((emp, idx) => (
                <li
                  key={emp.id}
                  onClick={() => handleSelect(emp)}
                  onMouseEnter={() => setHighlightIndex(idx)}
                  className={`px-4 py-3 cursor-pointer transition-colors duration-100 border-b last:border-b-0 ${highlightIndex === idx
                      ? "bg-cyan-500/10"
                      : "hover:bg-slate-700/30"
                    }`}
                  style={{ borderColor: "rgba(51, 65, 85, 0.15)" }}
                >
                  <p
                    className={`text-sm font-medium truncate ${highlightIndex === idx ? "text-cyan-300" : "text-gray-200"
                      }`}
                  >
                    {emp.name}
                  </p>
                  <div className="flex items-center gap-3 mt-0.5">
                    {emp.nik && (
                      <span className="text-[11px] font-mono text-slate-500">
                        {emp.nik}
                      </span>
                    )}
                    {emp.department && (
                      <>
                        <span className="text-slate-700">•</span>
                        <span className="text-[11px] text-slate-500">
                          {emp.department}
                        </span>
                      </>
                    )}
                    {emp.position && (
                      <>
                        <span className="text-slate-700">•</span>
                        <span className="text-[11px] text-slate-500">
                          {emp.position}
                        </span>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Footer info */}
          {!loading && filtered.length > 0 && (
            <div
              className="px-4 py-2 text-[11px] text-slate-500 text-center"
              style={{ borderTop: "1px solid rgba(51, 65, 85, 0.2)" }}
            >
              {filtered.length} karyawan ditemukan
              <span className="text-slate-600 mx-1">•</span>
              Gunakan ↑↓ untuk navigasi, Enter untuk pilih
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ============================================
   KOMPONEN: Time Input dengan Toggle "Sekarang"
   ============================================ */
const TimeInput = ({
  label,
  icon: Icon,
  value,
  onChange,
  useNow,
  onToggleNow,
  disabled,
  error,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider inline-flex items-center gap-1.5">
          <Icon className="text-cyan-500/70" />
          {label}
        </label>

        {/* Toggle Sekarang */}
        <button
          type="button"
          onClick={onToggleNow}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all duration-200 ${useNow
              ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/25"
              : "bg-slate-700/30 text-slate-500 border border-slate-600/20 hover:text-slate-300 hover:border-slate-500/30"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          <FaClock className="text-[10px]" />
          {useNow ? "Waktu Sekarang" : "Gunakan Sekarang"}
        </button>
      </div>

      <div className="relative">
        <input
          type="time"
          value={useNow ? getNowTimeInput() : value}
          onChange={(e) => onChange(e.target.value)}
          disabled={useNow || disabled}
          step="1"
          className={`w-full px-3.5 py-3 rounded-xl text-sm font-mono text-gray-100 outline-none transition-all duration-200 ${error
              ? "border border-red-500/40 bg-red-500/5"
              : "border border-slate-600/30 bg-slate-800/40 hover:border-slate-500/40 focus:border-cyan-500/40 focus:bg-slate-800/60"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          style={{ backdropFilter: "blur(8px)" }}
        />

        {/* Live clock indicator when "Sekarang" active */}
        {useNow && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-[11px] text-cyan-400/70 font-medium">LIVE</span>
          </div>
        )}
      </div>

      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
      {useNow && (
        <p className="mt-1.5 text-[11px] text-slate-500">
          Waktu akan otomatis menggunakan jam saat ini saat disimpan
        </p>
      )}
    </div>
  );
};

/* ============================================
   KOMPONEN: Status Selector
   ============================================ */
const StatusSelector = ({ value, onChange, error }) => {
  const options = [
    {
      key: "Tepat Waktu",
      label: "Tepat Waktu",
      color: "emerald",
      icon: "✓",
    },
    {
      key: "Terlambat",
      label: "Terlambat",
      color: "amber",
      icon: "⚠",
    },
    { key: "Izin", label: "Izin", color: "blue", icon: "📋" },
    { key: "Alpha", label: "Alpha", color: "red", icon: "✕" },
  ];

  const colorMap = {
    emerald: {
      active: "bg-emerald-500/15 border-emerald-500/30 text-emerald-400 shadow-emerald-500/10",
      ring: "ring-emerald-500/20",
    },
    amber: {
      active: "bg-amber-500/15 border-amber-500/30 text-amber-400 shadow-amber-500/10",
      ring: "ring-amber-500/20",
    },
    blue: {
      active: "bg-blue-500/15 border-blue-500/30 text-blue-400 shadow-blue-500/10",
      ring: "ring-blue-500/20",
    },
    red: {
      active: "bg-red-500/15 border-red-500/30 text-red-400 shadow-red-500/10",
      ring: "ring-red-500/20",
    },
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
        <span className="inline-flex items-center gap-1.5">
          <FaTag className="text-cyan-500/70" />
          Status Kehadiran
        </span>
      </label>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {options.map((opt) => {
          const isActive = value === opt.key;
          const c = colorMap[opt.color];

          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              className={`relative flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-sm font-semibold transition-all duration-200 ${isActive
                  ? `${c.active} shadow-lg ring-2 ${c.ring}`
                  : "bg-slate-800/30 border-slate-600/20 text-slate-400 hover:border-slate-500/30 hover:text-slate-300 hover:bg-slate-800/50"
                }`}
            >
              <span className="text-base">{opt.icon}</span>
              <span className="text-xs">{opt.label}</span>
              {isActive && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-cyan-500 rounded-full flex items-center justify-center">
                  <i className="fa-solid fa-check text-[8px] text-white" />
                </span>
              )}
            </button>
          );
        })}
      </div>

      {error && <p className="mt-1.5 text-xs text-red-400">{error}</p>}
    </div>
  );
};

/* ============================================
   KOMPONEN: Live Clock Mini
   ============================================ */
const LiveClockMini = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
      style={{
        background: "rgba(6, 182, 212, 0.06)",
        border: "1px solid rgba(6, 182, 212, 0.12)",
      }}
    >
      <span
        className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"
        style={{ boxShadow: "0 0 8px rgba(6,182,212,0.5)" }}
      />
      <span className="text-lg font-mono font-bold text-cyan-400 tracking-tight">
        {formatTimeWIB(time)}
      </span>
      <span className="text-[11px] text-slate-500 hidden sm:inline">
        WIB · {time.toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}
      </span>
    </div>
  );
};

/* ============================================
   KOMPONEN UTAMA: AdminPresenceForm
   ============================================ */
export const PresenceForm = ({

}) => {

  const [showSidebar, setShowSidebar] = useState(false);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  /* ---------- Form State ---------- */
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [date, setDate] = useState(getNowDateInput());
  const [timeIn, setTimeIn] = useState("08:00:00");
  const [timeOut, setTimeOut] = useState("17:00:00");
  const [useNowIn, setUseNowIn] = useState(false);
  const [useNowOut, setUseNowOut] = useState(false);
  const [location, setLocation] = useState("Kantor Pusat");
  const [status, setStatus] = useState("Tepat Waktu");
  const [notes, setNotes] = useState("");


  /* ---------- Tipe Absensi ---------- */
  const [presenceType, setPresenceType] = useState("full"); // "full" | "in_only" | "out_only"

  /* ---------- Errors ---------- */
  const [errors, setErrors] = useState({});

  /* ---------- Toast ---------- */
  const [toast, setToast] = useState(null);

  /* ---------- Validation ---------- */
  const validate = () => {
    const errs = {};

    if (!selectedEmployee) {
      errs.employee = "Pilih karyawan terlebih dahulu";
    }

    if (!date) {
      errs.date = "Tanggal wajib diisi";
    }

    if (presenceType === "full" || presenceType === "in_only") {
      if (!useNowIn && !timeIn) {
        errs.timeIn = "Waktu masuk wajib diisi";
      }
    }

    if (presenceType === "full" || presenceType === "out_only") {
      if (!useNowOut && !timeOut) {
        errs.timeOut = "Waktu keluar wajib diisi";
      }
    }

    if (presenceType === "full" && !useNowIn && !useNowOut && timeIn && timeOut) {
      if (timeOut <= timeIn) {
        errs.timeOut = "Waktu keluar harus setelah waktu masuk";
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const [employees] = useState([
    { id: 1, name: "Ahmad Fauzi", nik: "EMP-001", department: "IT", position: "Frontend Developer" },
    { id: 2, name: "Siti Nurhaliza", nik: "EMP-002", department: "HRD", position: "HR Manager" },
    { id: 3, name: "Budi Santoso", nik: "EMP-003", department: "Finance", position: "Akuntan" },
    { id: 4, name: "Dewi Lestari", nik: "EMP-004", department: "Marketing", position: "Digital Marketer" },
    { id: 5, name: "Rizky Pratama", nik: "EMP-005", department: "IT", position: "Backend Developer" },
    { id: 6, name: "Nadia Putri", nik: "EMP-006", department: "Design", position: "UI/UX Designer" },
  ]);

  const [submitLoading, setSubmitLoading] = useState(false);

  // const handleSubmit = async (payload) => {
  //   setSubmitLoading(true);
  //   // Simulasi API call
  //   await new Promise((resolve) => setTimeout(resolve, 1500));
  //   console.log("Payload yang dikirim:", payload);
  //   setSubmitLoading(false);
  // };

  /* ---------- Submit Handler ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      setToast({
        message: "Mohon lengkapi form dengan benar",
        type: "error",
      });
      return;
    }

    const payload = {
      employee_id: selectedEmployee,
      date,
      time_in: useNowIn ? formatTimeWIB(new Date()) : timeIn,
      time_out:
        presenceType === "in_only"
          ? null
          : useNowOut
            ? formatTimeWIB(new Date())
            : timeOut,
      location,
      status,
      notes: notes.trim() || null,
      presence_type: presenceType,
    };

    try {
      // await onSubmit?.(payload);
      // setToast({
      //   message: "Berhasil menyimpan data absensi karyawan",
      //   type: "success",
      // });
      handleReset();
    } catch (err) {
      setToast({
        message:
          err?.response?.data?.message ||
          "Gagal menyimpan data absensi",
        type: "error",
      });
    }
  };

  /* ---------- Reset Form ---------- */
  const handleReset = () => {
    setSelectedEmployee(null);
    setDate(getNowDateInput());
    setTimeIn("08:00:00");
    setTimeOut("17:00:00");
    setUseNowIn(false);
    setUseNowOut(false);
    setLocation("Kantor Pusat");
    setStatus("Tepat Waktu");
    setNotes("");
    setPresenceType("full");
    setErrors({});
  };

  /* ---------- Presence Type Config ---------- */
  const typeConfig = {
    full: { label: "Masuk & Keluar", icon: FaLink, desc: "Catat waktu masuk dan keluar" },
    in_only: { label: "Hanya Masuk", icon: FaRightToBracket, desc: "Catat waktu masuk saja" },
    out_only: { label: "Hanya Keluar", icon: FaRightFromBracket, desc: "Catat waktu keluar saja" },
  };

  return (
    <>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-6"
        >
          {/* ===== Header Bar ===== */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
                <FaUserClock className="text-cyan-400" />
                Form Absensi Manual
              </h3>
              <p className="text-sm text-slate-400 mt-1">
                Lakukan absensi atas nama karyawan lain
              </p>
            </div>
            <LiveClockMini />
          </div>

          {/* ===== Tipe Absensi ===== */}
          <div>
            <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
              Jenis Pencatatan
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(typeConfig).map(([key, cfg]) => {
                const isActive = presenceType === key;
                const IconComp = cfg.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPresenceType(key)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-all duration-200 ${isActive
                        ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-300 shadow-lg shadow-cyan-500/5 ring-1 ring-cyan-500/20"
                        : "bg-slate-800/30 border-slate-600/20 text-slate-400 hover:border-slate-500/30 hover:text-slate-300 hover:bg-slate-800/50"
                      }`}
                  >
                    <IconComp className={`text-lg flex-shrink-0 ${isActive ? "text-cyan-400" : ""}`} />
                    <div>
                      <p className="text-sm font-semibold">{cfg.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{cfg.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ===== Grid Utama ===== */}
          <div
            className="rounded-2xl p-5 lg:p-6 space-y-5"
            style={{
              background: "rgba(15, 23, 42, 0.4)",
              border: "1px solid rgba(51, 65, 85, 0.25)",
              backdropFilter: "blur(12px)",
            }}
          >
            {/* Pilih Karyawan */}
            <EmployeeSearch
              employees={employees}
              selected={selectedEmployee}
              onSelect={setSelectedEmployee}
              loading={employeesLoading}
              error={errors.employee}
            />

            {/* Tanggal & Lokasi */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <FaCalendarDay className="text-cyan-500/70" />
                    Tanggal
                  </span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  max={getNowDateInput()}
                  className={`w-full px-3.5 py-3 rounded-xl text-sm text-gray-100 outline-none transition-all duration-200 ${errors.date
                      ? "border border-red-500/40 bg-red-500/5"
                      : "border border-slate-600/30 bg-slate-800/40 hover:border-slate-500/40 focus:border-cyan-500/40 focus:bg-slate-800/60"
                    }`}
                  style={{
                    backdropFilter: "blur(8px)",
                    colorScheme: "dark",
                  }}
                />
                {errors.date && (
                  <p className="mt-1.5 text-xs text-red-400">{errors.date}</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                  <span className="inline-flex items-center gap-1.5">
                    <FaLocationDot className="text-cyan-500/70" />
                    Lokasi
                  </span>
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Contoh: Kantor Pusat, WFH, Dinas Luar"
                  className="w-full px-3.5 py-3 rounded-xl text-sm text-gray-100 placeholder-slate-500 outline-none border border-slate-600/30 bg-slate-800/40 hover:border-slate-500/40 focus:border-cyan-500/40 focus:bg-slate-800/60 transition-all duration-200"
                  style={{ backdropFilter: "blur(8px)" }}
                />
              </div>
            </div>

            {/* Waktu Masuk & Keluar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {(presenceType === "full" || presenceType === "in_only") && (
                <TimeInput
                  label="Waktu Masuk"
                  icon={FaRightToBracket}
                  value={timeIn}
                  onChange={setTimeIn}
                  useNow={useNowIn}
                  onToggleNow={() => setUseNowIn(!useNowIn)}
                  disabled={false}
                  error={errors.timeIn}
                />
              )}

              {(presenceType === "full" || presenceType === "out_only") && (
                <TimeInput
                  label="Waktu Keluar"
                  icon={FaRightFromBracket}
                  value={timeOut}
                  onChange={setTimeOut}
                  useNow={useNowOut}
                  onToggleNow={() => setUseNowOut(!useNowOut)}
                  disabled={false}
                  error={errors.timeOut}
                />
              )}

              {/* Info card saat hanya satu sisi */}
              {presenceType === "in_only" && (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    background: "rgba(6, 182, 212, 0.04)",
                    border: "1px dashed rgba(6, 182, 212, 0.15)",
                  }}
                >
                  <FaRightFromBracket className="text-slate-600 text-lg flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      Waktu keluar belum dicatat
                    </p>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      Karyawan dapat melakukan absen keluar nanti
                    </p>
                  </div>
                </div>
              )}

              {presenceType === "out_only" && (
                <div
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    background: "rgba(6, 182, 212, 0.04)",
                    border: "1px dashed rgba(6, 182, 212, 0.15)",
                  }}
                >
                  <FaRightToBracket className="text-slate-600 text-lg flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      Waktu masuk sudah ada di sistem
                    </p>
                    <p className="text-[11px] text-slate-600 mt-0.5">
                      Hanya mencatat waktu keluar
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <StatusSelector
              value={status}
              onChange={setStatus}
              error={errors.status}
            />

            {/* Catatan / Keterangan */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Catatan <span className="text-slate-600 normal-case">(Opsional)</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Tambahkan catatan jika diperlukan, misalnya: Izin karena sakit, Dinas ke client, dll."
                rows={3}
                className="w-full px-3.5 py-3 rounded-xl text-sm text-gray-100 placeholder-slate-500 outline-none border border-slate-600/30 bg-slate-800/40 hover:border-slate-500/40 focus:border-cyan-500/40 focus:bg-slate-800/60 transition-all duration-200 resize-none"
                style={{ backdropFilter: "blur(8px)" }}
              />
            </div>
          </div>

          {/* ===== Preview Ringkasan ===== */}
          {selectedEmployee && (
            <div
              className="rounded-2xl p-5"
              style={{
                background: "rgba(6, 182, 212, 0.03)",
                border: "1px solid rgba(6, 182, 212, 0.1)",
              }}
            >
              <p className="text-xs font-semibold text-cyan-400/70 uppercase tracking-wider mb-3">
                <i className="fa-solid fa-eye mr-1.5" />
                Preview Data yang Akan Disimpan
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-bold">Karyawan</p>
                  <p className="text-sm text-gray-200 font-medium mt-0.5 truncate">
                    {employees.find((e) => e.id === selectedEmployee)?.name || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-bold">Tanggal</p>
                  <p className="text-sm text-gray-200 font-medium mt-0.5">
                    {new Date(date).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-bold">Waktu</p>
                  <p className="text-sm text-gray-200 font-mono mt-0.5">
                    {presenceType !== "out_only" && (
                      <span className="text-cyan-400">
                        {useNowIn ? "🔥 Sekarang" : timeIn}
                      </span>
                    )}
                    {presenceType === "full" && " → "}
                    {presenceType !== "in_only" && (
                      <span className={presenceType === "full" ? "text-slate-400" : "text-cyan-400"}>
                        {useNowOut ? "🔥 Sekarang" : timeOut}
                      </span>
                    )}
                    {presenceType === "in_only" && (
                      <span className="text-slate-500"> (keluar belum)</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-bold">Status</p>
                  <p className="text-sm font-medium mt-0.5">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${status === "Tepat Waktu"
                          ? "bg-emerald-500/12 text-emerald-400 border border-emerald-500/20"
                          : status === "Terlambat"
                            ? "bg-amber-500/12 text-amber-400 border border-amber-500/20"
                            : status === "Izin"
                              ? "bg-blue-500/12 text-blue-400 border border-blue-500/20"
                              : "bg-red-500/12 text-red-400 border border-red-500/20"
                        }`}
                    >
                      {status}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ===== Action Buttons ===== */}
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 pt-2">
            {/* {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-slate-300 border border-slate-600/30 bg-slate-800/30 hover:bg-slate-800/50 hover:border-slate-500/30 transition-all duration-200"
            >
              <FaXmark />
              Batal
            </button>
          )} */}

            <button
              type="button"
              onClick={handleReset}
              className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold text-slate-400 border border-slate-600/20 bg-slate-800/20 hover:bg-slate-800/40 hover:text-slate-300 transition-all duration-200"
            >
              <FaRotate />
              Reset Form
            </button>

            <button
              type="submit"
              disabled={submitLoading}
              className="flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-sm font-bold text-white bg-cyan-600 hover:bg-cyan-500 shadow-lg shadow-cyan-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {submitLoading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  Menyimpan...
                </>
              ) : (
                <>
                  <FaCheckDouble />
                  Simpan Absensi
                </>
              )}
            </button>
          </div>
        </form>
      </DashboardLayout>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};
